import { compact } from 'lodash';
import { useMemo } from 'react';

import { ButtonGroupInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { useFormContext } from '@/components/react-hook-form';
import {
  useWaterfallGrantorDeathDisplay,
  useWaterfallGrantorDeathDisplayInput,
} from '@/hooks/useWaterfallGrantorDeathDisplay';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AfterDeath } from '@/types/schema';

import {
  HypotheticalSaleLoanFormPaths,
  HypotheticalSaleLoanFormShape,
} from '../EstateWaterfallHypotheticalSaleLoanModal.types';
import { useSaleLoanFormContext } from '../Form/context/SaleLoanForm.context';

export function SaleLoanIllustrationScenarioSelector() {
  const { control } = useFormContext<HypotheticalSaleLoanFormShape>();
  const { isTwoClientHousehold } = useHouseholdDetailsContext();
  const { waterfall, simulatedWaterfall } = useSaleLoanFormContext();

  // Get the input from the simulated waterfall if it exists, otherwise use the waterfall
  const grantorDeathDisplayInput: useWaterfallGrantorDeathDisplayInput =
    useMemo(() => {
      return {
        firstGrantorDeathYear:
          simulatedWaterfall?.visualizationWithSaleLoanProjections
            .materializedInputParams.firstGrantorDeathYear ??
          waterfall?.firstGrantorDeathYear,
        secondGrantorDeathYear:
          simulatedWaterfall?.visualizationWithSaleLoanProjections
            .materializedInputParams.secondGrantorDeathYear ??
          waterfall?.secondGrantorDeathYear,
        firstGrantorDeath: waterfall?.firstGrantorDeath,
      };
    }, [
      simulatedWaterfall?.visualizationWithSaleLoanProjections
        .materializedInputParams.firstGrantorDeathYear,
      simulatedWaterfall?.visualizationWithSaleLoanProjections
        .materializedInputParams.secondGrantorDeathYear,
      waterfall?.firstGrantorDeath,
      waterfall?.firstGrantorDeathYear,
      waterfall?.secondGrantorDeathYear,
    ]);

  const tabs = useWaterfallGrantorDeathDisplay(grantorDeathDisplayInput);

  const scenarioOptions = useMemo<ButtonGroupInputOption<AfterDeath>[]>(() => {
    return compact([
      {
        value: AfterDeath.None,
        display: tabs[AfterDeath.None],
      },
      {
        value: AfterDeath.First,
        display: tabs[AfterDeath.First],
      },
      isTwoClientHousehold && {
        value: AfterDeath.Second,
        display: tabs[AfterDeath.Second],
      },
    ]);
  }, [isTwoClientHousehold, tabs]);

  return (
    <FormAwareButtonGroup
      control={control}
      options={scenarioOptions}
      fieldName={
        'illustrationScenario' as const satisfies HypotheticalSaleLoanFormPaths
      }
      label="Year of illustration"
      hideLabel
    />
  );
}
